import { useCallback, useEffect, useState } from 'react'
import { functions } from '../../../config/firebase'
import getDoc from '../../../utils/db/getDoc'
import { getCatalogFeedRuntime } from '../../../config/catalogFeedsConfig'
import {
  extractCallableErrorMessage,
  normalizeSyncDate,
  normalizeSyncDatesDoc,
  parseLastSyncFromResponse,
} from '../syncDateUtils'

const appendCacheBust = (url) => {
  if (!url) {
    return url
  }

  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}t=${Date.now()}`
}

export const useCatalogXlsFeed = (catalog) => {
  const runtime = getCatalogFeedRuntime(catalog)

  const [lastGeneratedAt, setLastGeneratedAt] = useState(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [rowsCount, setRowsCount] = useState(null)
  const [counts, setCounts] = useState(null)
  const [freshFeedUrls, setFreshFeedUrls] = useState(null)

  const reloadFeedDate = useCallback(async () => {
    if (!runtime?.dateKey) {
      return {}
    }

    const doc = await getDoc({ path: 'api', docId: 'requestsDates' })
    const dates = normalizeSyncDatesDoc(doc)
    if (catalog === 'combined') setCounts(doc?.combinedFacebookFeedCounts ?? null)
    setLastGeneratedAt(dates[runtime.dateKey] ?? null)
    return dates
  }, [catalog, runtime?.dateKey])

  useEffect(() => {
    reloadFeedDate().catch(() => setErrorMessage('Не вдалося завантажити дату останньої генерації.'))
  }, [reloadFeedDate])

  const getPlatformDownloadUrl = useCallback(
    (platform) => {
      const freshUrl = freshFeedUrls?.[platform.id]?.downloadUrl
      const baseUrl = freshUrl || platform.downloadUrl
      return appendCacheBust(baseUrl)
    },
    [freshFeedUrls]
  )

  const getPlatformFeedUrl = useCallback(
    (platform) => freshFeedUrls?.[platform.id]?.feedUrl || platform.feedUrl,
    [freshFeedUrls]
  )

  const generateFacebookFeed = useCallback(async () => {
    if (!runtime || isGenerating) {
      return { ok: false }
    }

    setIsGenerating(true)
    setErrorMessage(null)

    try {
      const callable = functions.httpsCallable(runtime.callableName, {
        timeout: 300000,
      })
      const resp = await callable({ platform: 'facebook' })
      const parsedDate = parseLastSyncFromResponse(resp.data)

      if (parsedDate) {
        setLastGeneratedAt(parsedDate)
      } else {
        await reloadFeedDate()
      }

      if (typeof resp.data?.rowsCount === 'number') {
        setRowsCount(resp.data.rowsCount)
      }
      if (resp.data?.counts) setCounts(resp.data.counts)

      if (resp.data?.xlsxPublicUrl || resp.data?.publicUrl) {
        setFreshFeedUrls({
          facebook: {
            downloadUrl: resp.data.xlsxPublicUrl || null,
            feedUrl: resp.data.publicUrl || null,
          },
        })
      }

      return { ok: true, rowsCount: resp.data?.rowsCount }
    } catch (error) {
      console.error(`${runtime.callableName} failed:`, error)
      const code =
        typeof error.code === 'string' ? error.code.replace('functions/', '') : ''
      const message =
        code === 'not-found'
          ? 'Генерація фіду ще недоступна.'
          : extractCallableErrorMessage(
              error,
              code === 'failed-precondition'
                ? 'Немає товарів для фіду. Перевірте активні позиції, залишки та фото.'
                : code === 'permission-denied' || /cors/i.test(error?.message || '')
                  ? 'Немає доступу до генерації фіду.'
                  : `Помилка генерації фіду (${runtime.callableName}). Перегляньте логи Cloud Functions.`
            )
      setErrorMessage(message)
      return { ok: false, message }
    } finally {
      setIsGenerating(false)
    }
  }, [isGenerating, reloadFeedDate, runtime])

  return {
    runtime,
    lastGeneratedAt,
    isGenerating,
    errorMessage,
    rowsCount,
    counts,
    generateFacebookFeed,
    reloadFeedDate,
    getPlatformDownloadUrl,
    getPlatformFeedUrl,
  }
}
